import { FunctionComponent, useEffect, useState } from 'react';
import cn from 'classnames';
import {
  useForm,
  FormProvider,
  SubmitHandler,
  useFieldArray,
} from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Section,
  SectionHeading,
  TextField,
  NumberField,
  DateField,
  Address,
  TrashIcon,
} from '../../components';
import { PaIFormValues } from '../../types';
import { cleanIntakeDates, validateDateProvided } from '../../utils/intake';
import { successCallbackPath } from '../../utils/paths';
import { fetchApi } from '../../utils/fetchApi';
import { ManagedReqsByState } from './GenericIntake';
import { useScrollIntoView } from '../../hooks';
import { FormError } from '../../components/forms/FormError';
import { ApiStatus, IApiData } from '../../utils/types';

const EMPLOYEE_DEFAULT_VALUE = { first_name: null, last_name: null };

type PaIntakeProps = {
  onSubmitSuccess?: () => void;
  onEnterLater?: () => void;
  isFullWidth?: boolean;
  scrollOnMount?: boolean;
};

export const PaIntake: FunctionComponent<PaIntakeProps> = ({
  onSubmitSuccess,
  onEnterLater,
  isFullWidth,
  scrollOnMount,
}) => {
  const [scrollIntoViewRef] = useScrollIntoView(scrollOnMount);
  const [searchParams] = useSearchParams();
  const callbackUrl = searchParams.get('callback_url');

  const [hasSubmitError, setHasSubmitError] = useState<boolean>(false);

  const navigate = useNavigate();

  const formMethods = useForm<PaIFormValues>();
  const {
    watch,
    handleSubmit,
    formState: { errors },
    control,
  } = formMethods;

  const {
    fields: employeeFields,
    append: appendEmployees,
    remove: removeEmployee,
  } = useFieldArray({
    control,
    name: 'employees',
  });
  const watchEmployees = watch('employees', []);

  const numEstablishments = watch('num_pa_locations', 0);

  useEffect(() => {
    appendEmployees(EMPLOYEE_DEFAULT_VALUE, { shouldFocus: false });
  }, []);

  const handleSubmitSuccess = () =>
    onSubmitSuccess ? onSubmitSuccess() : navigate(`/state-setup/pa/success`);

  const onSubmit: SubmitHandler<PaIFormValues> = (data) => {
    setHasSubmitError(false);
    const cleanedData = cleanIntakeDates(data);
    const payload = { data: cleanedData };

    fetchApi({
      url: `/api/form_captures/pa`,
      method: 'POST',
      body: payload,
    }).then(({ status }: IApiData) => {
      if (status === ApiStatus.Error) {
        setHasSubmitError(true);
      } else if (ManagedReqsByState.pa) {
        const reqIds: string[] = ManagedReqsByState.pa;
        // Set the requirement to managed and in-progress
        fetchApi({
          url: `/api/requirements/batch`,
          method: 'PATCH',
          body: {
            patch: { operation: 'mark-as-managed' },
            data_ids: reqIds,
          },
        }).then(({ status: status2 }: IApiData) => {
          if (status2 === ApiStatus.Error) {
            setHasSubmitError(true);
          } else {
            // Create and start workflow for requirements
            fetchApi({
              url: `/api/workflows`,
              method: 'POST',
              body: {
                data_ids: reqIds,
                region_code: 'pa',
              },
            }).then(({ status: status3 }: IApiData) => {
              if (status3 === ApiStatus.Error) {
                setHasSubmitError(true);
              } else if (callbackUrl) {
                navigate(successCallbackPath(callbackUrl), { replace: true });
              } else {
                handleSubmitSuccess();
              }
            });
          }
        });
      } else {
        handleSubmitSuccess();
      }
    });
  };

  return (
    <Section className="flex flex-col items-center" ref={scrollIntoViewRef}>
      <SectionHeading
        className={cn('pb-4', {
          'w-full': isFullWidth,
          'w-96': !isFullWidth,
        })}
        text="Pennsylvania Employer Setup"
      />
      <FormProvider {...formMethods}>
        <form
          className={cn('my-8 ', {
            'w-full': isFullWidth,
            'w-96': !isFullWidth,
          })}
          onSubmit={handleSubmit(onSubmit)}
        >
          <TextField
            name="dba_name"
            label="Alternative Business Name (optional)"
            placeholder="Acme's Trade Name"
            description="Trade name or DBA if different from the company's legal business name."
            error={errors.dba_name}
          />
          <DateField
            name="activity_start_date"
            label="First Pennsylvania Employee Start Date"
            description="This date can be in the future in anticipation of an employee's coming start date."
            error={errors.activity_start_date}
            reactFormConfig={{
              required: 'This is required',
              validate: {
                isDate: validateDateProvided,
              },
            }}
          />
          <DateField
            name="first_payroll_date"
            label="Date of First Payroll"
            error={errors.first_payroll_date}
            reactFormConfig={{
              required: 'This is required',
              validate: {
                isDate: validateDateProvided,
              },
            }}
          />
          <NumberField
            name="num_pa_locations"
            label="Number of Pennsylvania Locations"
            description="If you only have employees working from home enter 0."
            min="0"
            error={errors.num_pa_locations}
            reactFormConfig={{
              required: 'This is required',
            }}
          />
          {numEstablishments > 0 && (
            <div className="mb-8">
              <div>
                <label className="mb-2 block font-semibold">
                  Pennsylvania Address
                </label>
                <p className="mb-4 text-sm text-gray-600">
                  Pennsylvania requires your primary location address. The
                  address cannot be a P.O. Box.
                </p>
                <Address
                  error={errors.physical_address}
                  name="physical_address"
                  defaultState="Pennsylvania"
                />
              </div>
            </div>
          )}
          <NumberField
            name="est_quarter_gross_wages"
            label="Estimated Gross Wages Per Quarter"
            description="Please provide the estimated gross wages (to the nearest thousand) per quarter in Pennsylvania."
            min="0"
            step="1000"
            isCurrency
            error={errors.est_quarter_gross_wages}
            reactFormConfig={{
              required: 'This is required',
            }}
          />
          <div className="mb-8">
            <div>
              <div>
                <h2 className="block pb-2 text-xl font-semibold text-gray-700">
                  Your New Hire
                </h2>
                <p className="mb-4 text-sm text-gray-600">
                  Please add your new hires in Pennsylvania.
                </p>
                <ul>
                  {employeeFields.map((item: any, index) => {
                    return (
                      <li
                        key={item.id}
                        className="mb-4 rounded border bg-gray-100 p-4"
                      >
                        {/* HACK: This is suuuper awkward, but `register`
                        expects a string literal and typescript can't
                        figure it out without casting. */}
                        <TextField
                          name={`employees.${index}.first_name`}
                          data-testid={`employees.${index}.first_name`}
                          label="First name"
                          error={errors.employees?.[index]?.first_name}
                          reactFormConfig={{
                            required: 'This is required',
                          }}
                        />
                        <TextField
                          name={`employees.${index}.last_name`}
                          data-testid={`employees.${index}.last_name`}
                          label="Last name"
                          error={errors.employees?.[index]?.last_name}
                          reactFormConfig={{
                            required: 'This is required',
                          }}
                        />
                        <Address
                          name={`employees.${index}.physical_address`}
                          label="Address"
                          error={errors.employees?.[index]?.physical_address}
                          limitedStateOptions={['Pennsylvania']}
                        />
                        <div className="flex flex-row-reverse">
                          <Button
                            type="button"
                            variant="secondary"
                            leftIcon={<TrashIcon className="size-4" />}
                            onClick={() => removeEmployee(index)}
                          >
                            Delete
                          </Button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
            <Button
              type="button"
              variant="secondary"
              onClick={() => appendEmployees(EMPLOYEE_DEFAULT_VALUE)}
            >
              Add another new hire
            </Button>
            {watchEmployees.length === 0 && (
              <FormError
                margin="mt-1"
                errorMessage="You must add at least 1 new hire"
              />
            )}
          </div>
          <div className="mt-4 flex">
            <div className="flex gap-x-4">
              {onEnterLater && (
                <Button
                  type="button"
                  variant="grayTertiary"
                  size="large"
                  isFullWidth
                  onClick={onEnterLater}
                >
                  Enter later
                </Button>
              )}
              <Button type="submit" isFullWidth size="large">
                Submit
              </Button>
            </div>
            {hasSubmitError && <FormError />}
          </div>
        </form>
      </FormProvider>
    </Section>
  );
};

export default PaIntake;
